import { util } from 'utils'
import { strings } from 'context/LanguageContext/strings'
import { format } from './format'
import { isEmpty, orderTypes } from './appUtils'
import constants from './constants'
import { store } from '../config/redux'
import { promoValidate } from './promoValidate'

const checkValues = (VAL, DEFAULT) => {
    switch (VAL) {
        case 'null':
        case 'undefined':
        case null:
        case undefined:
            return DEFAULT
        default:
            return VAL
    }
}

const isValidCountry = country => country !== 'CA'

export const parse = {
    getAddressComponents: (array = []) => {
        const filteredComponents = array.filter(
            element =>
                element.types.includes('street_number') ||
                element.types.includes('route') ||
                element.types.includes('locality') ||
                element.types.includes('neighborhood') ||
                element.types.includes('administrative_area_level_1') ||
                element.types.includes('administrative_area_level_2') ||
                element.types.includes('postal_code') ||
                element.types.includes('country')
        )

        const mappedComponents = filteredComponents.reduce((acc, val) => {
            const mappedComponentKey = format.mapAddressFields(val.types[0])
            return {
                ...acc,
                [mappedComponentKey]:
                    mappedComponentKey === 'city' ? val.long_name : val.short_name,
            }
        }, {})

        // Destructure only required fields
        const { city, state, zip, county, country } = mappedComponents

        return {
            city,
            state,
            street: `${mappedComponents.street_number} ${mappedComponents.street}`,
            zip,
            county,
            country,
        }
    },
    getCompleteAddress: (addObj, mode) => {
        const parsedAddComponent = parse.getAddressComponents(addObj.address_components)
        const {
            place_id,
            geometry: {
                location: { lat: lattitude, lng: longitude },
            },
            formatted_address,
            name: nickname,
        } = addObj
        return {
            ...parsedAddComponent,
            place_id,
            lattitude: lattitude.toString(),
            longitude: longitude.toString(),
            formatted_address,
            nickname,
            apt: '',
            type: mode,
        }
    },
    getCommonFormattedAddress: (addObj, mode) => {
        const { address, city, county, state, zip, nickname, country } = addObj
        const isPickup = mode === orderTypes.PICKUP
        let modeType = orderTypes.PICKUP
        if (!isPickup) {
            modeType =
                addObj.type && addObj.type === orderTypes.PICKUP
                    ? orderTypes.PICKUP
                    : orderTypes.DELIVERY
        }
        return {
            formatted_address: address,
            apt: isPickup ? '' : addObj.apt,
            lattitude: isPickup ? addObj.lat.toString() : addObj.latitude.toString(),
            longitude: isPickup ? addObj.lon.toString() : addObj.longitude.toString(),
            nickname,
            city,
            state,
            street: isPickup ? address : addObj.street,
            zip,
            county,
            country,
            type: modeType,
        }
    },

    parseUserProfile: rawProfile => ({
        first_name: checkValues(rawProfile.first_name, ''),
        last_name: checkValues(rawProfile.last_name, ''),
        digital_id: checkValues(rawProfile.digital_id, ''),
        loyalty_id: checkValues(rawProfile.loyalty_id, ''),
        country: checkValues(rawProfile.country, ''),
        // phone_number: checkValues(JSON.stringify(rawProfile.phone_number), ''),
        phone_number: checkValues(
            rawProfile.phone_number && rawProfile.phone_number.toString(),
            ''
        ),
        email: checkValues(rawProfile.email, ''),
        enroll_date: checkValues(rawProfile.enroll_date, ''),
        verified_for_age: checkValues(rawProfile.verified_for_age, null),
        training_mode: checkValues(rawProfile.training_mode, false),
        signup_referrer_code: checkValues(rawProfile.signup_referrer_code, ''),
        rewards_points: checkValues(rawProfile.rewards_points, 0),
        active_orders_count: checkValues(rawProfile.active_orders_count, 0),
        required_terms: checkValues(rawProfile.required_terms, []),
        past_orders_count_by_type: {
            delivery: checkValues(rawProfile.orders.delivery, 0),
            pickup: checkValues(rawProfile.orders.pickup, 0),
        },
        subscriptions: checkValues(rawProfile.subscriptions, []),
        is_age_verified: checkValues(rawProfile.is_age_verified, false),
        has_acknowledged_catering_ftue: checkValues(rawProfile.has_acknowledged_catering_ftue, false),
    }),
    checkUserProfileFieldsPresent: profile => {
        const email = profile && profile.email ? profile.email : ''
        const first_name = profile && profile.first_name ? profile.first_name : ''
        const last_name = profile && profile.last_name ? profile.last_name : ''
        const phone_number = profile && profile.phone_number ? profile.phone_number : ''

        const missingFields = {
            emailMissing: !email || !email.length > 0,
            firstNameMissing: !first_name || !first_name.length > 0,
            lastNameMissing: !last_name || !last_name.length > 0,
            phoneNumberMissing: !phone_number || !phone_number.length > 0,
        }
        const hasMissingFields = Object.values(missingFields).includes(true)

        return {
            hasMissingFields,
            ...missingFields,
        }
    },
    parseCategories: rawCategory => {
        const extractRestrictedAge = category => {
            const { location } = store.getState()
            const { selectedAddress } = location.toJS()
            const userState = !isEmpty(selectedAddress) ? selectedAddress.state : null
            const { min_purchase_age } = category
            const keys = Object.keys(min_purchase_age)

            if (userState && min_purchase_age) {
                if (userState === keys.includes(userState)) {
                    return min_purchase_age[userState]
                }
                if (keys.includes('default')) {
                    return min_purchase_age.default
                }
            }

            return constants.defaultAge
        }

        const isTobaccoCategory = meta_tags => {
            return meta_tags && meta_tags.includes('tobacco') ? true : false
        }

        const category = {
            id: checkValues(rawCategory.id, ''),
            name: checkValues(rawCategory.name, ''),
            desc: checkValues(rawCategory.desc, ''),
            thumbnail: checkValues(rawCategory.thumbnail, ''),
            verify_age: checkValues(rawCategory.verify_age, false),
            full_image: checkValues(rawCategory.full_image, ''),
            long_desc: checkValues(rawCategory.long_desc, ''),
            small_image: checkValues(rawCategory.small_image, ''),
            min_purchase_age: constants.defaultAge,
            localizations_ui: checkValues(rawCategory.localizations_ui, ''),
        }

        if (rawCategory.hasOwnProperty('verify_age') && rawCategory.verify_age === true) {
            category.verify_age = true
        }
        if (rawCategory.min_purchase_age) {
            category.min_purchase_age = extractRestrictedAge(rawCategory)
        }
        if (rawCategory.meta_tags && rawCategory.meta_tags.length > 0) {
            category.meta_tags = rawCategory.meta_tags.map(tag => tag.toLowerCase())
        }

        if (isTobaccoCategory(rawCategory.meta_tags) || category.id.length <= 0) return null

        return category
    },

    parseProduct: rawProduct => {
        const product = {
            category_id: checkValues(rawProduct.category_id, ''),
            name: checkValues(rawProduct.name, ''),
            category: checkValues(rawProduct.category, ''),
            desc: checkValues(rawProduct.desc, ''),
            longDesc: checkValues(rawProduct.long_desc, ''),
            age_restricted: checkValues(
                rawProduct.hasOwnProperty('age_restricted') && rawProduct.age_restricted,
                true
            ),
            promo_price: checkValues(rawProduct.promo_price, null),
            price: checkValues(rawProduct.price, null),
            thumbnail: checkValues(rawProduct.thumbnail, ''),
            images: checkValues(rawProduct.images, []),
            tags: checkValues(rawProduct.tags, []),
            meta_tags: checkValues(rawProduct.meta_tags, []),
            product_id: checkValues(rawProduct.product_id || rawProduct.id, ''),
            calories: checkValues(rawProduct.calories, 'NaN'),
            quantity: checkValues(rawProduct.quantity, 0),
            availableQuantity: checkValues(rawProduct.availableQuantity, 0),
            age_restriction: checkValues(rawProduct.age_restriction, 21), // age for age restricted products
            available: checkValues(rawProduct.available, false),
            ignore_quantity: checkValues(rawProduct.ignore_quantity, false),
            limit_per_order: checkValues(rawProduct.limit_per_order, 0),
            lead_time: checkValues(rawProduct.lead_time, 0),
            availabilityMessage: checkValues(rawProduct.availabilityMessage, ''),
            verify_age: checkValues(rawProduct.verify_age, false),
            sale_hours_by_day: checkValues(rawProduct.sale_hours_by_day, null),
            matching_ids: checkValues(rawProduct.matching_ids, []),
            store_id: checkValues(rawProduct.store_id, ''),
            upc: checkValues(rawProduct.upc || rawProduct.id || rawProduct.product_id, null),
            upcs: checkValues(rawProduct.upcs, []),
            size_value: checkValues(rawProduct.size_value, ''),
            product_type: null, // For Bundles
            promos: checkValues(rawProduct.promos, []),
            displayNutritionInfo: checkValues(rawProduct.displayNutritionInfo, null),
            Ingredients: checkValues(rawProduct.Ingredients, []),
            promoShortDesc: checkValues(rawProduct.promoShortDesc, ''),
            promoLongDesc: checkValues(rawProduct.promoLongDesc, ''),
            products: null, // For Bundle items
            catalog_price: checkValues(rawProduct.catalog_price, null),
            slin: checkValues(rawProduct.slin, ''),
            promoValidation: checkValues(rawProduct.promoValidation, []),
            platter_item_flag: checkValues(rawProduct?.platter_item_flag, ''),
        }

        let meta_tags = []
        if (rawProduct.meta_tags && rawProduct.meta_tags.length > 0) {
            meta_tags = rawProduct.meta_tags.map(tag => tag.toLowerCase())
            product.meta_tags = meta_tags
        }

        if (product.product_id.length <= 0 || util.isTobaccoOrCbdProduct(meta_tags)) {
            return null
        }

        if (rawProduct.category_id) product.category_id = rawProduct.category_id

        if (rawProduct.name) product.name = rawProduct.name

        if (rawProduct.category) product.category = rawProduct.category

        if (rawProduct.desc) product.desc = rawProduct.desc

        if (rawProduct.long_desc) product.long_desc = rawProduct.long_desc

        if (rawProduct?.age_restricted === false) {
            product.age_restricted = false
        }

        if (rawProduct.promos && rawProduct.promos.length > 0 && rawProduct.promos[0].promo_price) {
            product.promo_price = rawProduct.promos[0].promo_price
        } else if (rawProduct.promo_price) product.promo_price = rawProduct.promo_price

        if (rawProduct.price) product.price = rawProduct.price

        if (rawProduct.catalog_price) product.catalog_price = rawProduct.catalog_price

        if (rawProduct.thumbnail && rawProduct.thumbnail.replace(/ /g, '').length) {
            product.thumbnail = rawProduct.thumbnail
        }

        if (rawProduct.images) product.images = rawProduct.images

        if (rawProduct.tags) product.tags = rawProduct.tags

        if (
            rawProduct.hasOwnProperty('calories') &&
            rawProduct.calories &&
            Number.isNaN(rawProduct.calories) === false
        ) {
            const cals = rawProduct.calories
            const x = cals.toString()
            if (Math.sign(cals) < 0) {
                product.calories = `< ${x.replace('-', '')} ${strings.Cals}`
            } else {
                product.calories = `${x} ${strings.Cals}`
            }
        } else {
            product.calories = ''
        }

        if (rawProduct.quantity) product.quantity = rawProduct.quantity

        if (rawProduct.availableQuantity) product.availableQuantity = rawProduct.availableQuantity

        if (rawProduct.age_restriction) product.age_restriction = rawProduct.age_restriction

        if (rawProduct.available) product.available = rawProduct.available

        if (rawProduct.ignore_quantity) product.ignore_quantity = rawProduct.ignore_quantity

        if (rawProduct.limit_per_order) product.limit_per_order = rawProduct.limit_per_order

        if (rawProduct.availabilityMessage)
            product.availabilityMessage = rawProduct.availabilityMessage

        if (rawProduct?.verify_age) {
            product.verify_age = true
        }

        if (rawProduct.sale_hours_by_day) product.sale_hours_by_day = rawProduct.sale_hours_by_day

        if (rawProduct.matching_ids) product.matching_ids = rawProduct.matching_ids

        if (rawProduct.upc) product.upc = rawProduct.upc

        if (rawProduct.upcs) product.upcs = rawProduct.upcs

        if (rawProduct.size_value)
            product.size_value = rawProduct.size_value ? rawProduct.size_value : ''

        if (rawProduct.displayNutritionInfo) {
            product.displayNutritionInfo = rawProduct.displayNutritionInfo
        }
        if (rawProduct.Ingredients) {
            product.Ingredients = rawProduct.Ingredients
        }

        if (
            rawProduct.promos &&
            rawProduct.promos.length > 0 &&
            promoValidate(rawProduct.promos)[0]?.promo_short_desc
        ) {
            product.promoShortDesc = promoValidate(rawProduct.promos)[0]?.promo_short_desc
        } else if (rawProduct.promoShortDesc) product.promoShortDesc = rawProduct.promoShortDesc

        if (
            rawProduct.promos &&
            rawProduct.promos.length > 0 &&
            promoValidate(rawProduct.promos)[0]?.promo_long_desc
        ) {
            product.promoLongDesc = promoValidate(rawProduct.promos)[0]?.promo_long_desc
        } else if (rawProduct.promoLongDesc) product.promoLongDesc = rawProduct.promoLongDesc

        if (rawProduct?.promos) {
            product.promoValidation = promoValidate(rawProduct.promos).length
        }

        // Add count to track the locally added quantity
        product.count = 0

        return product
    },

    parseHomePageResponse: payload => {
        const response = {
            itemsInProgress: false,
            displayOrder: null,
            banners: [],
            specialBanners: [],
            promotions: [],
            deals: [],
            purchases: [],
            recommendations: [],
            categories: [],
            categoriesTitle: null,
            customCategoryProducts: [],
        }
        const _payload = payload.response
        const categoryItems = []
        const categories = []
        let deals = []
        let purchases = []
        let recommendation = {}
        const banners = []
        const specialBanners = []
        const promoBanners = []
        const displayOrder = _payload.display_order
        const ignoreItems = [
            'deals',
            'categories',
            'promo_banner',
            'banners',
            'recommendation',
            'user_purchased_items',
            'special_banner',
        ]
        const ignoreCategories = ['48466']
        const categoriesTitle =
            _payload.categories && _payload.categories.display_name
                ? _payload.categories.display_name
                : null
        if (displayOrder) {
            for (const key in displayOrder) {
                if (!_payload.hasOwnProperty(displayOrder[key])) {
                    delete displayOrder[key] // Correctly deleting the dynamic key
                } else if (!ignoreItems.includes(displayOrder[key])) {
                    if (_payload.hasOwnProperty(displayOrder[key])) {
                        if (!ignoreCategories.includes(displayOrder[key])) {
                            const productsContent = _payload[displayOrder[key]].content
                            const parsedContent = []
                            for (let i = 0; i < productsContent.length; i++) {
                                const parsedProduct = parse.parseProduct(productsContent[i])
                                if (parsedProduct) parsedContent.push(parsedProduct)
                            }
                            const customCategory = {
                                display_name: _payload[displayOrder[key]].display_name || '',
                                content: parsedContent,
                                category_details:
                                    _payload[displayOrder[key]].category_details || {},
                            }
                            categoryItems.push(customCategory)
                        }
                    }
                } else if (displayOrder[key] === 'categories') {
                    if (!_payload[displayOrder[key]].content) {
                        delete displayOrder.key
                    } else {
                        _payload[displayOrder[key]].content.forEach(category => {
                            const parsedCategory = parse.parseCategories(category)
                            if (parsedCategory) {
                                categories.push(parsedCategory)
                            }
                        })
                    }
                } else if (displayOrder[key] === 'deals') {
                    if (!_payload[displayOrder[key]].content) {
                        delete displayOrder.key
                    } else {
                        deals = _payload[displayOrder[key]]
                        const parsedProducts = []

                        deals.content.forEach(product => {
                            const parsedProduct = parse.parseProduct(product)
                            if (parsedProduct) {
                                parsedProducts.push(parsedProduct)
                            }
                        })
                        deals.content = parsedProducts
                    }
                } else if (displayOrder[key] === 'user_purchased_items') {
                    if (!_payload[displayOrder[key]].content) {
                        delete displayOrder.key
                    } else {
                        purchases = _payload[displayOrder[key]]
                        const parsedProducts = []

                        purchases.content.forEach(product => {
                            const parsedProduct = parse.parseProduct(product)
                            if (parsedProduct) {
                                parsedProducts.push(parsedProduct)
                            }
                        })
                        purchases.content = parsedProducts
                    }
                } else if (displayOrder[key] === 'recommendation') {
                    if (!_payload[displayOrder[key]].content) {
                        delete displayOrder.key
                    } else {
                        recommendation = _payload[displayOrder[key]]
                        const parsedProducts = []
                        recommendation.content.forEach(product => {
                            const parsedProduct = parse.parseProduct(product)
                            if (parsedProduct) {
                                parsedProducts.push(parsedProduct)
                            }
                        })
                        recommendation.content = parsedProducts
                    }
                } else if (displayOrder[key] === 'banners') {
                    if (!_payload[displayOrder[key]].content) {
                        delete displayOrder.key
                    } else {
                        for (let i = 0; i < _payload[displayOrder[key]].content.length; i++) {
                            if (
                                _payload[displayOrder[key]].content[i] &&
                                _payload[displayOrder[key]].content[i].message &&
                                _payload[displayOrder[key]].content[i].message.img &&
                                _payload[displayOrder[key]].content[i].message.img[0]
                            ) {
                                banners.push(_payload[displayOrder[key]].content[i])
                            }
                        }
                    }
                } else if (displayOrder[key] === 'promo_banner') {
                    if (!_payload[displayOrder[key]].content) {
                        delete displayOrder.key
                    } else {
                        for (let i = 0; i < _payload[displayOrder[key]].content.length; i++) {
                            if (
                                _payload[displayOrder[key]].content[i] &&
                                _payload[displayOrder[key]].content[i].image &&
                                _payload[displayOrder[key]].content[i].image !== '' &&
                                _payload[displayOrder[key]].content[i].target &&
                                _payload[displayOrder[key]].content[i].target.entity !== 'Donations'
                            ) {
                                promoBanners.push(_payload[displayOrder[key]].content[i])
                            }
                        }
                    }
                } else if (displayOrder[key] === 'special_banner') {
                    if (!_payload[displayOrder[key]].content) {
                        delete displayOrder.key
                    } else {
                        for (let i = 0; i < _payload[displayOrder[key]].content.length; i++) {
                            if (
                                _payload[displayOrder[key]].content[i] &&
                                (_payload[displayOrder[key]].content[i].message ||
                                    _payload[displayOrder[key]].content[i].msg_input) &&
                                (_payload[displayOrder[key]].content[i].message.img ||
                                    _payload[displayOrder[key]].content[i].msg_input.img) &&
                                (_payload[displayOrder[key]].content[i].message.img[0] ||
                                    _payload[displayOrder[key]].content[i].msg_input.img[0]) &&
                                (_payload[displayOrder[key]].content[i].message.img[0] ||
                                    _payload[displayOrder[key]].content[i].msg_input.img[0]) !== ''
                            ) {
                                specialBanners.push(_payload[displayOrder[key]].content[i])
                            }
                        }
                    }
                }
            }

            response.itemsInProgress = false
            response.displayOrder = displayOrder
            response.banners = banners
            response.promotions = promoBanners
            response.specialBanners = specialBanners
            response.deals = deals
            response.purchases = purchases
            response.recommendations = recommendation
            response.categories = categories
            response.categoriesTitle = categoriesTitle
            response.customCategoryProducts = categoryItems
        }
        return response
    },
    parseHomePageCategoryResponse: payload => {
        const response = {
            itemsInProgress: false,
            categories: [],
        }
        const _payload = payload.response
        const categories = []
        const categoriesTitle =
            _payload.categories && _payload.categories.display_name
                ? _payload.categories.display_name
                : null
        const displayOrder = _payload.display_order
        if (displayOrder) {
            for (const key in displayOrder) {
                if (!_payload.hasOwnProperty(displayOrder[key])) {
                    delete displayOrder.key // test
                } else if (displayOrder[key] === 'categories') {
                    if (!_payload[displayOrder[key]].content) {
                        delete displayOrder.key
                    } else {
                        _payload[displayOrder[key]].content.forEach(category => {
                            const parsedCategory = parse.parseCategories(category)
                            if (parsedCategory) {
                                categories.push(parsedCategory)
                            }
                        })
                    }
                }
            }
            response.categoriesTitle = categoriesTitle
            response.categories = categories
            response.itemsInProgress = false
            response.displayOrder = displayOrder
            response.banners = []
            response.promotions = []
            response.specialBanners = []
            response.deals = []
            response.purchases = []
            response.recommendations = []
            response.categories = categories
            response.categoriesTitle = categoriesTitle
            response.customCategoryProducts = []
        }
        return response
    },
    parseRenewalsResponse: data => {
        const { renewals } = data || {}
        if (renewals.length) {
            return renewals
        }
    },
}

export const UTMTags = {
    utm_source: true,
    utm_medium: true,
    utm_campaign: true,
    utm_term: true,
    utm_content: true,
}

export const getUtmSearch = (search, updatedSearch) => {
    if (isEmpty(search)) {
        return updatedSearch ? `?${updatedSearch}` : ''
    }
    let str = search

    if (search[0] === '?') {
        str = str.split('?')[1]
    }

    let arr = str.split('&')

    arr = arr.map(a => a.split('='))
    arr = arr.filter(([key]) => UTMTags[key])

    str = arr.map(a => a.join('=')).join('&')

    if (!updatedSearch) {
        return str ? `?${str}` : ''
    }

    return str ? `?${updatedSearch}&${str}` : updatedSearch ? `?${updatedSearch}` : null
}

export const excludeUtmSearchParams = search => {
    let str = search

    if (search[0] === '?') {
        str = str.split('?')[1]
    }

    let arr = str.split('&')

    arr = arr.map(a => a.split('='))
    arr = arr.filter(([key]) => !UTMTags[key])

    str = arr.map(a => a.join('=')).join('&')

    return `?${str}`
}
